/**
 * Store for managing account setup state and validation
 * @category Pinia
 * @module AccountSetupStore
 */
import { $rollbar } from '@/boot/rollbar'
import { isValidAddress } from '@/utils/validator.util'
import { PERSONAL } from '@/constants/account.const'

export const useAccountSetupStore = defineStore('accountSetup', () => {
  const $q = useQuasar()
  const { featureFlags } = useFeatureFlagStore()

  const currentStepIndex = ref(0)

  const accountSignUpType = computed(() => useUserStore().user?.signUpType)
  const accountSignUpTypeIsPersonal = computed(() => accountSignUpType.value === PERSONAL)
  const accountSetupSignUpTypeFailed = ref(false)

  const accountSetupTermsStepIsDone = computed(() => {
    return Boolean(useUserStore().user?.termsAccepted)
  })

  const accountSetupBillingStepIsDone = computed(() => {
    return Boolean(useUserStore().user?.billingCompleted)
  })

  const accountSetupUserProfileStepIsDone = computed(() => {
    const { firstName, lastName, phone } = useUserStore().user || {}
    return Boolean(!!firstName && !!lastName && !!phone && !!accountSignUpType.value)
  })

  const accountSetupCommunicationPrefsStepIsDone = computed(() => {
    const { smsOptIn, emailOptIn } = useUserStore().user || {}
    return Boolean(smsOptIn || emailOptIn)
  })

  const accountSetupPersonalShippingAddressStepIsDone = computed(() => {
    return isValidAddress(useUserStore().userShippingAddress)
  })

  // PEG-921 (epic PEG-823): the derived account-completion gate. Completion is a
  // pure read-time derivation of the minimum identity fields — Terms accepted,
  // First Name, Last Name, Phone, Email — plus a valid address (and, for BUSINESS
  // accounts, the Business Profile step — see businessAccountSetupIsDone below),
  // and is explicitly
  // INDEPENDENT of billing, communication preferences, saved payment, and
  // auto-checkout (those marketplace requirements move to a bid-time gate,
  // PEG-923). There is no persisted setup_complete flag; correctness is derived
  // by construction across both entry paths (returner hydration / direct signup).
  // accountSetupUserProfileStepIsDone (which also requires signUpType) still drives
  // the profile STEP in the setup UI; this gate is the completion definition.
  //
  // accountSetupMinimumIdentityIsDone is the account-type-agnostic identity core
  // (Terms + First + Last + Phone + Email). The "valid address" portion is applied
  // per account type by the completion gates below, because the two signup flows
  // collect DIFFERENT addresses: the personal flow saves the user's personal
  // shipping address (user.shipping*), while the business flow saves a business
  // OFFICE address (userBusiness.addresses[type=OFFICE]) and never touches
  // user.shipping*. Requiring the personal shipping address for a business account
  // would leave a fully-onboarded business user derived-incomplete with no UI step
  // to satisfy it, trapping them in the nav-guard redirect loop.
  const accountSetupMinimumIdentityIsDone = computed(() => {
    const { firstName, lastName, phone, email } = useUserStore().user || {}
    return Boolean(accountSetupTermsStepIsDone.value && !!firstName && !!lastName && !!phone && !!email)
  })

  const accountSetupBusinessProfileStepIsDone = computed(() => {
    const { type, name, email, phone } = useUserStore().userBusiness || {}
    return Boolean(!!type && !!name && !!email && !!phone)
  })

  const accountSetupBusinessOfficeAddressStepIsDone = computed(() => {
    return useUserStore().userBusiness?.addresses?.some(add => add.addressType === 'OFFICE' && isValidAddress(add)) || false
  })

  const personalAccountSetupIsDone = computed(() => {
    return Boolean(
      useUserStore().userHasPersonalAccount &&
      accountSetupMinimumIdentityIsDone.value &&
      accountSetupPersonalShippingAddressStepIsDone.value
    )
  })

  const businessAccountSetupIsDone = computed(() => {
    return Boolean(
      useUserStore().userHasBusinessAccount &&
      accountSetupMinimumIdentityIsDone.value &&
      // PEG-823 (PR #450 review): the Business Profile step (setupSteps[2]) is a
      // REQUIRED step in the business flow. Marking completion on identity +
      // OFFICE address alone left a business user with terms + identity + office
      // address but no business profile fields derived-complete, so the
      // completed-account nav guard redirected them away from /account/setup and
      // they could never reach the still-required Business Profile step. Require
      // it here so completion matches the required step set.
      accountSetupBusinessProfileStepIsDone.value &&
      accountSetupBusinessOfficeAddressStepIsDone.value
    )
  })

  const accountSetupIsDone = computed(() => {
    return Boolean(
      (useUserStore().userHasPersonalAccount && personalAccountSetupIsDone.value) ||
      (useUserStore().userHasBusinessAccount && businessAccountSetupIsDone.value)
    )
  })

  // PEG-921 / AC8: completion is the derived gate (accountSetupIsDone), no longer
  // user.billingCompleted. AccountSetupCompleteStep uses this to decide whether to
  // run finishSetup() side effects on land.
  const accountSetupIsComplete = computed(() => accountSetupIsDone.value)

  const setupSteps = computed(() => {
    let steps = [
      {
        done: accountSetupTermsStepIsDone.value,
      },
      {
        title: '',
        icon: 'fas fa-user',
        done: accountSetupUserProfileStepIsDone.value,
      },
    ]

    if (accountSignUpTypeIsPersonal.value) {
      steps = steps.concat([
        {
          title: `${featureFlags.fulfillmentDelivery?.enabled ? 'Default Shipping' : 'Primary'} Address`,
          icon: 'fas fa-home',
          done: accountSetupPersonalShippingAddressStepIsDone.value,
        },
        {
          title: 'Communication Preferences',
          icon: 'far fa-envelope',
          done: accountSetupCommunicationPrefsStepIsDone.value,
        },
      ])
    } else {
      steps = steps.concat([
        {
          title: 'Business Profile',
          icon: 'fas fa-building',
          done: accountSetupBusinessProfileStepIsDone.value,
        },
        { title: 'Office Address', icon: 'fas fa-location-dot', done: accountSetupBusinessOfficeAddressStepIsDone.value },
        {
          title: 'Communication Preferences',
          icon: 'far fa-envelope',
          done: accountSetupCommunicationPrefsStepIsDone.value,
        },
      ])
    }

    steps = steps.concat([
      {
        title: 'Wallet & Checkout',
        subtitle: 'Set up your payment and billing preferences',
        icon: 'fas fa-credit-card',
        done: accountSetupBillingStepIsDone.value,
        isFinishStep: true,
      },
      { done: accountSetupIsDone.value },
    ])

    return steps
  })

  async function setAccountSignUpType(signUpType) {
    try {
      await useUserStore().updateUser({ signUpType })
      accountSetupSignUpTypeFailed.value = false
      return { success: true }
    } catch (error) {
      accountSetupSignUpTypeFailed.value = true
      $rollbar.error('AccountSetup: Failed to update account type', error)
      $q.notify({ type: 'error', message: 'Failed to update account type. Please try again.' })
      return { success: false }
    }
  }

  async function startSetup() {
    $q.loading.show()
    try {
      await useUserStore().updateUser({ termsAccepted: true })
    } catch (error) {
      $rollbar.error('AccountSetup: Failed to start setup', error)
      $q.notify({ type: 'error', message: 'Something went wrong. Please try again.' })
    } finally {
      $q.loading.hide()
    }
  }

  // PEG-921 / AC8: completion is purely derived from the minimum identity fields,
  // so finishSetup no longer writes billingCompleted as the completion signal.
  // Billing / payment / auto-checkout are collected (and persisted by their own
  // steps) but are no longer the finish gate; they are enforced at bid time
  // (PEG-923). Kept as a no-op hook so the complete-step's on-land call site and
  // any future finish side effects have a single home.
  async function finishSetup() {}

  /**
   * Reset account setup state (called on logout)
   */
  function resetState() {
    currentStepIndex.value = 0
    accountSetupSignUpTypeFailed.value = false
  }

  return {
    // state
    currentStepIndex,
    setupSteps,
    accountSignUpType,
    // computed
    accountSignUpTypeIsPersonal,
    accountSetupSignUpTypeFailed,
    accountSetupTermsStepIsDone,
    accountSetupBillingStepIsDone,
    accountSetupUserProfileStepIsDone,
    accountSetupPersonalShippingAddressStepIsDone,
    accountSetupMinimumIdentityIsDone,
    accountSetupBusinessProfileStepIsDone,
    accountSetupCommunicationPrefsStepIsDone,
    accountSetupBusinessOfficeAddressStepIsDone,
    personalAccountSetupIsDone,
    businessAccountSetupIsDone,
    accountSetupIsDone,
    accountSetupIsComplete,
    // actions
    startSetup,
    finishSetup,
    setAccountSignUpType,
    resetState,
  }
})
